import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from './AuthContext';

// ── Types ─────────────────────────────────────────────────────

interface ThemeConfig {
  globalDefaultUrl: string | null;
  overrides: Record<string, string>;
}

interface BackgroundContextValue {
  backgroundUrl: string | null;
}

// ── Context ───────────────────────────────────────────────────

const BackgroundContext = createContext<BackgroundContextValue>({ backgroundUrl: null });

// ── Constants ─────────────────────────────────────────────────

/** Fallback background used when no admin-configured theme exists yet */
const DEFAULT_BACKGROUND_URL = '/ChaptersMainBackground1.jpeg';

// ── Helpers ───────────────────────────────────────────────────

/** Map route pathnames to Page_Key values */
export function getPageKeyFromPath(pathname: string): string | null {
  const routeToPageKey: Record<string, string> = {
    '/guest-toolkit': 'guest-toolkit',
    '/marketplace': 'marketplace',
    '/chapters': 'chapters',
    '/fun-facts': 'fun-facts',
  };
  return routeToPageKey[pathname] ?? null;
}

// ── Provider ──────────────────────────────────────────────────

export function BackgroundProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<ThemeConfig | null>(null);
  const { pathname } = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    apiClient<ThemeConfig>('/theme')
      .then(setConfig)
      .catch(() => setConfig(null));
  }, [user]);

  const backgroundUrl: string | null = (() => {
    if (!config) return DEFAULT_BACKGROUND_URL;
    const pageKey = getPageKeyFromPath(pathname);
    if (pageKey && config.overrides[pageKey]) {
      return config.overrides[pageKey];
    }
    return config.globalDefaultUrl ?? DEFAULT_BACKGROUND_URL;
  })();

  return (
    <BackgroundContext.Provider value={{ backgroundUrl }}>
      {children}
    </BackgroundContext.Provider>
  );
}

// ── Hook ──────────────────────────────────────────────────────

export function useBackground(): BackgroundContextValue {
  return useContext(BackgroundContext);
}
